import {
  AfterViewInit,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  Input,
  OnDestroy,
  Renderer2,
  ViewContainerRef,
  inject,
} from '@angular/core';

import { _AbcTooltipComponent } from './abc-tooltip/abc-tooltip.component';

@Directive({
  selector: '[abcTooltip]',
  standalone: true,
})
export class AbcTooltipDirective implements AfterViewInit, OnDestroy {
  @Input() public elementClassName = `.dx-texteditor-input`;
  @Input() public toolTipContentTemplate: string;

  private static readonly TRUNCATION_TOLERANCE = 1;

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private tooltipComponent!: ComponentRef<_AbcTooltipComponent>;

  private removeMouseEnter?: () => void;
  private removeMouseLeave?: () => void;

  ngAfterViewInit(): void {
    this.createTooltipComponent();

    // DevExtreme renders internal DOM asynchronously
    requestAnimationFrame(() => {
      this.attachEvents();
    });
  }

  ngOnDestroy(): void {
    this.removeMouseEnter?.();
    this.removeMouseLeave?.();
    if (this.tooltipComponent) {
      this.tooltipComponent.destroy();
    }
  }

  private attachEvents(): void {
    const host = this.elementRef.nativeElement;

    this.removeMouseEnter = this.renderer.listen(host, 'mouseenter', () => {
      this.handleMouseEnter();
    });

    this.removeMouseLeave = this.renderer.listen(host, 'mouseleave', () => {
      this.hideTooltip();
    });
  }

  private handleMouseEnter(): void {
    requestAnimationFrame(() => {
      const target = this.getTargetElement();

      if (!target) {
        return;
      }

      const isTruncated = this.isTextTruncated(target);

      if (!isTruncated && !this.toolTipContentTemplate) {
        this.hideTooltip();
        return;
      }

      this.showTooltip(target);
    });
  }

  private getTargetElement(): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector(this.elementClassName);
  }

  private showTooltip(target: HTMLElement): void {
    this.createTooltipComponent();

    const tooltip = this.tooltipComponent.instance;
    const contentTemplate = this.toolTipContentTemplate || this.extractText(target);

    tooltip.target = target;
    tooltip.contentTemplate = contentTemplate;
    tooltip.visible = !!tooltip.contentTemplate;
  }

  private hideTooltip(): void {
    if (!this.tooltipComponent) {
      return;
    }

    this.tooltipComponent.instance.visible = false;
  }

  private extractText(target: HTMLElement): string {
    if (target instanceof HTMLInputElement) {
      return target.value?.trim() ?? '';
    }

    return target.textContent?.trim() ?? '';
  }

  private isTextTruncated(element: HTMLElement): boolean {
    if (element instanceof HTMLInputElement) {
      return this.isInputTextTruncated(element);
    }

    return element?.scrollWidth > element?.clientWidth;
  }

  private isInputTextTruncated(input: HTMLInputElement): boolean {
    const value = input.value;

    if (!value?.trim()) {
      return false;
    }

    const style = getComputedStyle(input);

    const canvas = document.createElement('canvas');

    const context = canvas.getContext('2d');

    if (!context) {
      return false;
    }

    context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    const textWidth = context.measureText(value).width;
    const availableWidth = input.clientWidth;
    const hasDomOverflow = input.scrollWidth > input.clientWidth;
    return hasDomOverflow || textWidth > availableWidth + AbcTooltipDirective.TRUNCATION_TOLERANCE;
  }

  private createTooltipComponent(): void {
    const target = this.getTargetElement();

    if (this.isTextTruncated(target) || !!this.toolTipContentTemplate) {
      if (this.tooltipComponent) {
        return;
      }

      this.tooltipComponent = this.viewContainerRef.createComponent(_AbcTooltipComponent);

      const domElement = (this.tooltipComponent.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

      /**
       * Avoid appending inside DevExtreme DOM
       */
      document.body.appendChild(domElement);
    }
  }
}
